import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, MapPin, ChevronDown, Plus, Minus, Loader2, X, Image as ImageIcon, ImageOff, Bookmark } from 'lucide-react';
import { ordersApi, MenuItem, Establishment } from '../services/ordersApi';
import { geocoderApi, GeoSuggestion, ResolvedAddress } from '../services/geocoderApi';
import { LocationSuggestionBanner } from '../components/LocationSuggestionBanner';
import { AddressMapPicker } from '../components/AddressMapPicker';
import { ActiveOrderBanner } from '../components/ActiveOrderBanner';
import { MenuFavoriteButton } from '../components/MenuFavoriteButton';
import { MenuFavoritesOnboarding } from '../components/MenuFavoritesOnboarding';
import { useCart } from '../context/CartContext';
import { useUiPreferencesStore } from '../store/uiPreferencesStore';
import { useMenuFavoritesStore } from '../store/menuFavoritesStore';
import { pickTerminalGroupId } from '../utils/terminalGroupSync';
import { terminalGroupsQueryOptions } from '../queries/terminalGroups';
import { menuQueryOptions } from '../queries/menu';
import { operatingStatusQueryOptions } from '../queries/operatingStatus';
import { MENU_MOCK_MODE } from '../data/mockMenu';
import { appPath } from '../routes/appPaths';
import type { TerminalGroup } from '../services/ordersApi';

const HOME_MENU_STAGGER_KEY = 'theories_stagger_home_menu';

const MOCK_TERMINAL_GROUPS: TerminalGroup[] = [
  {
    id: 'mock-terminal',
    name: 'Теория',
    address: 'Долгопрудный, Первомайская 9/4',
    isAlive: true,
  },
];

function shouldPlayHomeMenuStagger(): boolean {
  try {
    return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(HOME_MENU_STAGGER_KEY) !== '1';
  } catch {
    return true;
  }
}

export default function HomeScreen() {
  const menuShowImages = useUiPreferencesStore((s) => s.menuShowImages);
  const setMenuShowImages = useUiPreferencesStore((s) => s.setMenuShowImages);
  const favoriteIds = useMenuFavoritesStore((s) => s.ids);
  const [showBanner, setShowBanner] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [selectedEstablishment, setSelectedEstablishment] = useState<string>('');

  const [addressInput, setAddressInput] = useState('');
  const [suggestions, setSuggestions] = useState<GeoSuggestion[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [deliveryCheckError, setDeliveryCheckError] = useState<string | null>(null);
  const [deliveryResolved, setDeliveryResolved] = useState<ResolvedAddress | null>(null);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const suggestTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const suggestBoxRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const chipsRowRef = useRef<HTMLDivElement>(null);
  const scrollLockUntil = useRef(0);

  const navigate = useNavigate();
  const {
    add,
    increment,
    decrement,
    items,
    selectedTerminalGroupId,
    setSelectedTerminalGroupId,
    deliveryMode,
    setDeliveryMode,
    deliveryAddress,
    setDeliveryAddress,
  } = useCart();

  const { data: apiTerminalGroups = [], isFetched: apiTerminalsLoaded } = useQuery({
    ...terminalGroupsQueryOptions(),
    enabled: !MENU_MOCK_MODE && deliveryMode === 'pickup',
  });
  const terminalGroups = useMemo(
    () => (MENU_MOCK_MODE ? MOCK_TERMINAL_GROUPS : apiTerminalGroups),
    [apiTerminalGroups],
  );
  const terminalsLoaded = MENU_MOCK_MODE || apiTerminalsLoaded;

  const { data: menuCategories = [] } = useQuery({
    ...menuQueryOptions(),
    // В мок-режиме меню не зависит от точек iiko
    enabled: MENU_MOCK_MODE || !!selectedEstablishment,
  });
  const { data: operatingStatus } = useQuery(operatingStatusQueryOptions());
  const scheduleClosed = !MENU_MOCK_MODE && operatingStatus && !operatingStatus.isOpen;

  const playMenuStagger = shouldPlayHomeMenuStagger();

  useEffect(() => {
    if (MENU_MOCK_MODE) {
      setEstablishments([
        { id: 'mock-est', name: 'Теория · Долгопрудный', address: 'Первомайская 9/4' },
      ]);
      setSelectedEstablishment('mock-est');
      return;
    }
    ordersApi.getEstablishments()
      .then(data => {
        setEstablishments(data);
        if (data.length > 0) {
          setSelectedEstablishment(data[0].id);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (deliveryMode !== 'pickup' || terminalGroups.length === 0) return;
    setSelectedTerminalGroupId(prev => pickTerminalGroupId(terminalGroups, prev));
  }, [deliveryMode, terminalGroups, setSelectedTerminalGroupId]);

  useEffect(() => {
    if (deliveryAddress) {
      setAddressInput(deliveryAddress.formattedAddress ?? `${deliveryAddress.city}, ${deliveryAddress.street}, ${deliveryAddress.house}`);
      setDeliveryResolved({
        city: deliveryAddress.city,
        street: deliveryAddress.street,
        house: deliveryAddress.house,
        yandexAddressId: deliveryAddress.yandexAddressId ?? '',
        formattedAddress: deliveryAddress.formattedAddress ?? '',
      } as ResolvedAddress);
    }
  }, [deliveryAddress]);

  const handleAddressInput = useCallback((value: string) => {
    setAddressInput(value);
    setDeliveryCheckError(null);
    if (!value.trim()) {
      setSuggestions([]);
      return;
    }
    clearTimeout(suggestTimerRef.current);
    suggestTimerRef.current = setTimeout(async () => {
      setSuggestLoading(true);
      try {
        const res = await geocoderApi.suggest(value);
        setSuggestions(res);
      } finally {
        setSuggestLoading(false);
      }
    }, 350);
  }, []);

  const handleSelectSuggestion = useCallback(async (s: GeoSuggestion) => {
    setSuggestions([]);
    setAddressInput(s.title + (s.subtitle ? `, ${s.subtitle}` : ''));
    setSuggestLoading(true);
    setDeliveryCheckError(null);
    try {
      const resolved = await geocoderApi.resolve(s.uri);
      if (!resolved || !resolved.house) {
        setDeliveryCheckError('Укажите дом. Выберите точный адрес из подсказок.');
        setSuggestLoading(false);
        return;
      }
      setDeliveryResolved(resolved);
      const check = await ordersApi.checkDelivery({
        city: resolved.city,
        street: resolved.street,
        house: resolved.house,
      });
      if (!check.allowed) {
        setDeliveryCheckError('Доставка по этому адресу недоступна');
        setDeliveryAddress(null);
      } else {
        const terminalGroupId = check.terminalGroups?.[0] ?? '';
        setDeliveryAddress(
          {
            city: resolved.city,
            street: resolved.street,
            house: resolved.house,
            yandexAddressId: resolved.yandexAddressId,
            formattedAddress: resolved.formattedAddress,
          },
          terminalGroupId,
        );
        setAddressInput(resolved.formattedAddress || `${resolved.city}, ${resolved.street}, ${resolved.house}`);
      }
    } finally {
      setSuggestLoading(false);
    }
  }, [setDeliveryAddress]);

  const handleClearAddress = useCallback(() => {
    setAddressInput('');
    setSuggestions([]);
    setDeliveryResolved(null);
    setDeliveryCheckError(null);
    setDeliveryAddress(null);
  }, [setDeliveryAddress]);

  const allItems = menuCategories.flatMap(cat => cat.items);
  const q = searchQuery.trim().toLowerCase();
  const favoriteSet = useMemo(() => new Set(favoriteIds), [favoriteIds]);

  /** Всегда все категории; поиск / избранное сужают позиции внутри секций */
  const visibleSections = menuCategories
    .map((cat) => ({
      ...cat,
      items: cat.items.filter((item) => {
        if (showFavoritesOnly && !favoriteSet.has(item.id)) return false;
        if (q && !item.name.toLowerCase().includes(q)) return false;
        return true;
      }),
    }))
    .filter((cat) => cat.items.length > 0);

  useEffect(() => {
    if (visibleSections.length === 0) return;
    if (!activeCategory || !visibleSections.some((c) => c.id === activeCategory)) {
      setActiveCategory(visibleSections[0].id);
    }
  }, [visibleSections, activeCategory]);

  useEffect(() => {
    if (!playMenuStagger || allItems.length === 0) return;
    const ms = Math.min(allItems.length * 40 + 400, 2500);
    const t = window.setTimeout(() => {
      try {
        sessionStorage.setItem(HOME_MENU_STAGGER_KEY, '1');
      } catch {
        /* ignore */
      }
    }, ms);
    return () => clearTimeout(t);
  }, [playMenuStagger, allItems.length]);

  useEffect(() => {
    if (visibleSections.length === 0 || q) return;

    const syncActiveFromScroll = () => {
      if (Date.now() < scrollLockUntil.current) return;
      const doc = document.documentElement;
      const nearBottom = window.scrollY + window.innerHeight >= doc.scrollHeight - 48;
      if (nearBottom) {
        const lastId = visibleSections[visibleSections.length - 1].id;
        setActiveCategory((prev) => (prev === lastId ? prev : lastId));
        return;
      }
      const marker = window.innerHeight * 0.32;
      let currentId = visibleSections[0].id;
      for (const cat of visibleSections) {
        const el = sectionRefs.current[cat.id];
        if (!el) continue;
        if (el.getBoundingClientRect().top <= marker) currentId = cat.id;
      }
      setActiveCategory((prev) => (prev === currentId ? prev : currentId));
    };

    syncActiveFromScroll();
    window.addEventListener('scroll', syncActiveFromScroll, { passive: true });
    return () => window.removeEventListener('scroll', syncActiveFromScroll);
  }, [visibleSections, q]);

  useEffect(() => {
    if (!activeCategory || q) return;
    const row = chipsRowRef.current;
    const chip = chipRefs.current[activeCategory];
    if (!row || !chip) return;
    const left = chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2;
    row.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [activeCategory, q]);

  function cartQty(id: string) {
    return items.find(i => i.id === id)?.quantity ?? 0;
  }

  function addItem(item: MenuItem) {
    add({
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: 1,
      source: 'api' as const,
      ...(item.image ? { image: item.image } : {}),
    });
  }

  function scrollToCategory(categoryId: string) {
    const el = sectionRefs.current[categoryId];
    if (!el) return;
    scrollLockUntil.current = Date.now() + 700;
    setActiveCategory(categoryId);
    const top = el.getBoundingClientRect().top + window.scrollY - 12;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0a0a0a' }}>
      <div className="max-w-md mx-auto w-full flex flex-col flex-1 relative">
        <div className="pt-3">
          <ActiveOrderBanner />
        </div>

        {scheduleClosed && (
          <div className="px-4 pb-2">
            <div
              className="rounded-2xl px-4 py-3 text-sm font-medium"
              style={{
                background: 'rgba(255,255,255,0.06)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              Кафе сейчас закрыто
              {operatingStatus?.nextOpenAt && (
                <span className="block mt-1 font-normal text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  Откроемся{' '}
                  {new Date(operatingStatus.nextOpenAt).toLocaleString('ru-RU', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Header card */}
        <div className="px-4 pt-5 pb-3">
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            {/* Establishment selector */}
            {establishments.length > 1 && (
              <div className="px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <select
                  value={selectedEstablishment}
                  onChange={e => setSelectedEstablishment(e.target.value)}
                  className="w-full bg-transparent text-sm font-medium outline-none"
                  style={{ color: '#fff' }}
                >
                  {establishments.map(est => (
                    <option key={est.id} value={est.id}>
                      {est.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Delivery toggle */}
            <div className="flex p-1.5 gap-1" style={{ background: 'rgba(255,255,255,0.04)' }}>
              {(['delivery', 'pickup'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => setDeliveryMode(mode)}
                  className="flex-1 py-2.5 text-sm font-semibold transition-all rounded-xl"
                  style={{
                    background: deliveryMode === mode ? '#fff' : 'transparent',
                    color: deliveryMode === mode ? '#0a0a0a' : 'rgba(255,255,255,0.45)',
                  }}
                >
                  {mode === 'delivery' ? 'Доставка' : 'Навынос'}
                </button>
              ))}
            </div>

            {/* Address / Pickup row */}
            {deliveryMode === 'pickup' ? (
              !terminalsLoaded ? (
                <div className="flex items-center gap-2 px-4 py-3">
                  <MapPin size={15} style={{ color: '#aaa' }} />
                  <span className="text-sm" style={{ color: '#aaa' }}>Загрузка точек...</span>
                </div>
              ) : terminalGroups.length === 0 ? (
                <div className="flex items-center gap-2 px-4 py-3">
                  <MapPin size={15} style={{ color: '#aaa' }} />
                  <span className="text-sm flex-1 text-left" style={{ color: '#888' }}>
                    Точки самовывоза не найдены
                  </span>
                </div>
              ) : terminalGroups.length === 1 ? (
                <div className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <MapPin size={15} style={{ color: 'rgba(255,255,255,0.45)' }} />
                    <span className="text-sm font-medium flex-1 text-left" style={{ color: '#fff' }}>
                      {terminalGroups[0].name}
                      {terminalGroups[0].address ? ` • ${terminalGroups[0].address}` : ''}
                    </span>
                  </div>
                  {terminalGroups[0].isAlive === false ? (
                    <p className="text-[11px] mt-2 pl-[1.75rem] leading-snug" style={{ color: '#a67c00' }}>
                      Ресторан временно не принимает заказы
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className="px-4 py-3">
                  <div className="relative">
                    <div className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 z-[1]">
                      <MapPin size={15} style={{ color: '#666' }} />
                    </div>
                    <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 z-[1]">
                      <ChevronDown size={15} style={{ color: '#aaa' }} />
                    </div>
                    <select
                      value={selectedTerminalGroupId}
                      onChange={e => setSelectedTerminalGroupId(e.target.value)}
                      className="w-full appearance-none pl-9 pr-9 py-0.5 bg-transparent text-sm font-medium outline-none cursor-pointer rounded-lg"
                      style={{ color: '#333' }}
                    >
                      {terminalGroups.map(g => (
                        <option key={g.id} value={g.id}>
                          {g.name}{g.address ? ` — ${g.address}` : ''}{g.isAlive === false ? ' · нет связи' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )
            ) : (
              <div className="relative" ref={suggestBoxRef}>
                <div className="flex items-center gap-2 px-4 py-3">
                  {suggestLoading
                    ? <Loader2 size={15} className="animate-spin shrink-0" style={{ color: '#aaa' }} />
                    : <MapPin size={15} className="shrink-0" style={{ color: deliveryAddress ? '#fff' : 'rgba(255,255,255,0.4)' }} />
                  }
                  <input
                    type="text"
                    value={addressInput}
                    onChange={e => handleAddressInput(e.target.value)}
                    placeholder="Улица и дом..."
                    className="flex-1 bg-transparent text-sm font-medium outline-none"
                    style={{ color: '#fff' }}
                  />
                  <button
                    onClick={() => setMapPickerOpen(true)}
                    className="shrink-0 active:scale-90 transition-all ml-1"
                    title="Выбрать на карте"
                  >
                    <MapPin size={15} style={{ color: deliveryAddress ? '#fff' : 'rgba(255,255,255,0.4)' }} strokeWidth={2.2} />
                  </button>
                  {addressInput && (
                    <button onClick={handleClearAddress} className="shrink-0 active:scale-90 transition-all">
                      <X size={15} style={{ color: '#aaa' }} />
                    </button>
                  )}
                </div>
                {deliveryCheckError && (
                  <p className="px-4 pb-2 text-[11px] font-medium" style={{ color: '#c0392b' }}>
                    {deliveryCheckError}
                  </p>
                )}
                <p className="px-4 pb-2 text-[11px] font-medium" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  Заказы на доставку временно не принимаются
                </p>
                {suggestions.length > 0 && (
                  <div
                    className="absolute left-0 right-0 top-full z-50 rounded-2xl overflow-hidden"
                    style={{
                      background: '#141414',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.45)',
                      border: '1px solid rgba(255,255,255,0.12)',
                    }}
                  >
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        onMouseDown={() => handleSelectSuggestion(s)}
                        className="w-full text-left px-4 py-3 flex flex-col gap-0.5 transition-colors"
                        style={{ borderBottom: i < suggestions.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}
                      >
                        <span className="text-sm font-medium" style={{ color: '#fff' }}>{s.title}</span>
                        {s.subtitle && <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{s.subtitle}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* You are here banner — only in delivery mode */}
        {deliveryMode === 'delivery' && (
          <LocationSuggestionBanner
            currentDeliveryAddress={deliveryAddress}
            onAccept={(addr, terminalGroupId) => {
              setDeliveryAddress(addr, terminalGroupId);
              setAddressInput(addr.formattedAddress ?? `${addr.city}, ${addr.street}, ${addr.house}`);
              setDeliveryCheckError(null);
            }}
          />
        )}

        {/* Search */}
        <div className="px-4 pb-3">
          <div
            className="flex items-center gap-3 rounded-2xl px-4 py-3"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <Search size={16} style={{ color: 'rgba(255,255,255,0.35)' }} className="shrink-0" />
            <input
              type="text"
              placeholder="Найти блюдо..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-sm"
              style={{ color: '#fff' }}
            />
          </div>
        </div>

        <div className="px-4">
          <MenuFavoritesOnboarding />
        </div>

        {/* Категории: скролл к разделу (меню всегда целиком) */}
        <div className="pb-3 flex items-center gap-2 px-4">
          <div
            ref={chipsRowRef}
            className="flex gap-2 overflow-x-auto no-scrollbar flex-1 min-w-0"
          >
            <button
              type="button"
              onClick={() => setShowFavoritesOnly((v) => !v)}
              className="shrink-0 px-3 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5"
              style={{
                background: showFavoritesOnly ? '#fff' : 'transparent',
                color: showFavoritesOnly ? '#0a0a0a' : 'rgba(255,255,255,0.4)',
                border: showFavoritesOnly ? 'none' : '1px solid rgba(255,255,255,0.12)',
                fontFamily: 'Manrope, sans-serif',
              }}
              aria-pressed={showFavoritesOnly}
            >
              <Bookmark size={14} fill={showFavoritesOnly ? '#0a0a0a' : 'none'} />
            </button>
            {!showFavoritesOnly &&
              visibleSections.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  ref={(node) => {
                    chipRefs.current[cat.id] = node;
                  }}
                  onClick={() => scrollToCategory(cat.id)}
                  className="shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all tracking-[0.04em] uppercase"
                  style={{
                    background: activeCategory === cat.id ? '#fff' : 'transparent',
                    color: activeCategory === cat.id ? '#0a0a0a' : 'rgba(255,255,255,0.4)',
                    border: activeCategory === cat.id ? 'none' : '1px solid rgba(255,255,255,0.12)',
                    fontFamily: 'Manrope, sans-serif',
                  }}
                >
                  {cat.name}
                </button>
              ))}
          </div>
          <button
            type="button"
            onClick={() => setMenuShowImages(!menuShowImages)}
            className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-90"
            title={menuShowImages ? 'Скрыть фото' : 'Показать фото'}
            aria-label={menuShowImages ? 'Скрыть фото' : 'Показать фото'}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: menuShowImages ? '#fff' : 'rgba(255,255,255,0.45)',
            }}
          >
            {menuShowImages ? <ImageIcon size={16} /> : <ImageOff size={16} />}
          </button>
        </div>

        {/* Меню — все секции подряд, плюс справа */}
        <div className="px-4 pb-44">
          {menuShowImages ? (
            visibleSections.map((category) => (
              <section
                key={category.id}
                ref={(node) => {
                  sectionRefs.current[category.id] = node;
                }}
                className="mb-8 scroll-mt-3"
              >
                <h2
                  className="text-[11px] font-semibold uppercase tracking-[0.28em] mb-3"
                  style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Manrope, sans-serif' }}
                >
                  {category.name}
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {category.items.map((item) => (
                    <DishCard
                      key={item.id}
                      item={item}
                      qty={cartQty(item.id)}
                      showImage
                      scheduleClosed={!!scheduleClosed}
                      onAdd={() => addItem(item)}
                      onInc={() => increment(item.id)}
                      onDec={() => decrement(item.id)}
                      onOpen={() => navigate(appPath(`/product/${item.id}`))}
                    />
                  ))}
                </div>
              </section>
            ))
          ) : (
            visibleSections.map((category) => (
              <section
                key={category.id}
                ref={(node) => {
                  sectionRefs.current[category.id] = node;
                }}
                className="mb-10 scroll-mt-3"
              >
                <h2
                  className="text-[11px] font-semibold uppercase tracking-[0.28em] mb-4"
                  style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Manrope, sans-serif' }}
                >
                  {category.name}
                </h2>
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  {category.items.map((item, index) => (
                    <MenuListRow
                      key={item.id}
                      item={item}
                      qty={cartQty(item.id)}
                      isLast={index === category.items.length - 1}
                      scheduleClosed={!!scheduleClosed}
                      onAdd={() => addItem(item)}
                      onInc={() => increment(item.id)}
                      onDec={() => decrement(item.id)}
                      onOpen={() => navigate(appPath(`/product/${item.id}`))}
                    />
                  ))}
                </div>
              </section>
            ))
          )}
          {visibleSections.length === 0 && (
            <div className="text-center py-16 text-sm" style={{ color: '#aaa', fontFamily: 'Manrope, sans-serif' }}>
              {showFavoritesOnly ? 'Пока нет избранного' : 'Ничего не найдено'}
            </div>
          )}
        </div>

        {/* Location banner */}
        <AnimatePresence>
          {showBanner && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              className="fixed z-40 px-4 left-0 right-0 mx-auto"
              style={{
                bottom: '100px',
                width: '100%',
                maxWidth: '28rem',
              }}
            >
              <div
                className="rounded-3xl p-5"
                style={{
                  background: 'rgba(10,10,10,0.9)',
                  backdropFilter: 'blur(24px)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
                }}
              >
                <p className="text-white font-semibold text-base mb-0.5">
                  Вы сейчас здесь?
                </p>
                <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  ГК, Боталка Яндекс
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowBanner(false)}
                    className="flex-1 py-3 rounded-2xl font-semibold text-sm transition-all active:scale-95"
                    style={{ background: '#fff', color: '#0a0a0a' }}
                  >
                    Да, верно
                  </button>
                  <button
                    onClick={() => setShowBanner(false)}
                    className="flex-1 py-3 rounded-2xl font-semibold text-sm transition-all active:scale-95"
                    style={{
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.6)',
                    }}
                  >
                    Изменить
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Map picker — full-screen overlay */}
      <AddressMapPicker
        open={mapPickerOpen}
        initialLat={deliveryResolved?.lat ?? undefined}
        initialLng={deliveryResolved?.lng ?? undefined}
        onClose={() => setMapPickerOpen(false)}
        onConfirm={async (addr) => {
          setAddressInput(addr.formattedAddress ?? `${addr.city}, ${addr.street}, ${addr.house}`);
          setDeliveryCheckError(null);
          try {
            const check = await ordersApi.checkDelivery({
              city: addr.city,
              street: addr.street,
              house: addr.house,
            });
            if (!check.allowed) {
              setDeliveryCheckError('Доставка по этому адресу недоступна');
              setDeliveryAddress(null);
            } else {
              setDeliveryAddress(addr, check.terminalGroups?.[0] ?? '');
            }
          } catch {
            setDeliveryAddress(addr);
          }
        }}
      />
    </div>
  );
}

function QtyStepper({
  qty,
  disabled,
  compact,
  onAdd,
  onInc,
  onDec,
}: {
  qty: number;
  disabled?: boolean;
  compact?: boolean;
  onAdd: () => void;
  onInc: () => void;
  onDec: () => void;
}) {
  const size = compact ? 32 : 36;
  const icon = compact ? 14 : 16;
  const qtyW = compact ? 18 : 20;
  const open = qty > 0;
  const spring = { type: 'spring' as const, stiffness: 480, damping: 34, mass: 0.7 };

  return (
    <motion.div
      className="flex items-center shrink-0 rounded-full overflow-hidden"
      style={{ height: size }}
      initial={false}
      animate={{
        width: open ? size * 2 + qtyW : size,
        backgroundColor: open ? '#ffffff' : 'rgba(255,255,255,0.1)',
        color: open ? '#0a0a0a' : '#ffffff',
      }}
      transition={spring}
      onClick={(e) => e.stopPropagation()}
    >
      <AnimatePresence initial={false}>
        {open && (
          <motion.button
            key="minus"
            type="button"
            onClick={onDec}
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: size }}
            exit={{ opacity: 0, width: 0 }}
            transition={spring}
            className="flex items-center justify-center active:scale-90 shrink-0 overflow-hidden"
            style={{ height: size }}
            aria-label="Убавить"
          >
            <Minus size={icon} />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {open && (
          <motion.span
            key="qty"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: qtyW }}
            exit={{ opacity: 0, width: 0 }}
            transition={spring}
            className="text-center text-xs font-bold tabular-nums shrink-0 overflow-hidden"
            style={{ fontFamily: 'Manrope, sans-serif', height: size, lineHeight: `${size}px` }}
          >
            {qty}
          </motion.span>
        )}
      </AnimatePresence>

      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          if (open) onInc();
          else onAdd();
        }}
        disabled={disabled}
        className="flex items-center justify-center active:scale-90 disabled:opacity-50 shrink-0"
        style={{ width: size, height: size }}
        aria-label={open ? 'Добавить ещё' : 'Добавить'}
      >
        <Plus size={icon} />
      </button>
    </motion.div>
  );
}

/** Строка как в зале: название ··· цена + степпер корзины */
function MenuListRow({
  item,
  qty,
  isLast,
  scheduleClosed,
  onAdd,
  onInc,
  onDec,
  onOpen,
}: {
  item: MenuItem;
  qty: number;
  isLast: boolean;
  scheduleClosed?: boolean;
  onAdd: () => void;
  onInc: () => void;
  onDec: () => void;
  onOpen: () => void;
}) {
  const addBlocked = item.isUnavailable || scheduleClosed;

  return (
    <div
      className={`flex items-start gap-2 py-3.5 ${item.isUnavailable ? 'opacity-40' : ''}`}
      style={{
        borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left active:opacity-70">
        <div className="flex items-baseline justify-between gap-3">
          <h3
            className="min-w-0 flex-1 text-[15px] font-semibold leading-snug"
            style={{
              color: '#fff',
              letterSpacing: '-0.01em',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            {item.name}
          </h3>
          <span
            className="shrink-0 text-[13px] tracking-[0.04em]"
            style={{ color: 'rgba(255,255,255,0.22)', fontFamily: 'Manrope, sans-serif' }}
            aria-hidden
          >
            ···
          </span>
          {item.price > 0 && (
            <span
              className="shrink-0 text-[15px] font-bold tabular-nums"
              style={{ color: '#fff', fontFamily: 'Manrope, sans-serif' }}
            >
              {item.price} ₽
            </span>
          )}
        </div>
        {item.description && (
          <p
            className="text-[12px] mt-1.5 line-clamp-2 leading-relaxed pr-2"
            style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
          >
            {item.description}
          </p>
        )}
        {item.isUnavailable && (
          <p
            className="text-[11px] font-semibold tracking-[0.08em] uppercase mt-1.5"
            style={{ color: '#a67c00', fontFamily: 'Manrope, sans-serif' }}
          >
            Нет в наличии
          </p>
        )}
      </button>
      <div className="mt-0.5 flex items-center gap-0.5">
        <MenuFavoriteButton itemId={item.id} size={14} />
        <QtyStepper
          qty={qty}
          disabled={addBlocked}
          onAdd={onAdd}
          onInc={onInc}
          onDec={onDec}
        />
      </div>
    </div>
  );
}

function DishCard({
  item,
  qty,
  showImage,
  scheduleClosed,
  onAdd,
  onInc,
  onDec,
  onOpen,
}: {
  item: MenuItem;
  qty: number;
  showImage: boolean;
  scheduleClosed?: boolean;
  onAdd: () => void;
  onInc: () => void;
  onDec: () => void;
  onOpen: () => void;
}) {
  const addBlocked = item.isUnavailable || scheduleClosed;

  return (
    <div
      className="rounded-3xl overflow-hidden cursor-pointer active:scale-95 transition-transform"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.1)',
        opacity: item.isUnavailable ? 0.5 : 1,
      }}
      onClick={onOpen}
    >
      {showImage && (
        <div className="relative w-full" style={{ paddingTop: '75%', background: 'rgba(255,255,255,0.04)' }}>
          {item.image ? (
            <img
              src={item.image}
              alt={item.name}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center" style={{ color: 'rgba(255,255,255,0.2)' }}>
              <ImageIcon size={28} />
            </div>
          )}
          <div
            className="absolute top-2 right-2 rounded-full"
            style={{ background: 'rgba(10,10,10,0.55)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuFavoriteButton itemId={item.id} size={16} className="w-8 h-8" />
          </div>
        </div>
      )}
      <div className="p-3">
        <div className="flex items-start gap-1 mb-1">
          <p className="font-semibold text-sm leading-tight flex-1 min-w-0" style={{ color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
            {item.name}
          </p>
          {!showImage && (
            <div onClick={(e) => e.stopPropagation()}>
              <MenuFavoriteButton itemId={item.id} size={15} className="w-7 h-7 -mr-1 -mt-0.5" />
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 mt-1">
          <span className="font-bold text-base" style={{ color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
            {item.price} ₽
          </span>
          <QtyStepper
            qty={qty}
            disabled={addBlocked}
            compact
            onAdd={onAdd}
            onInc={onInc}
            onDec={onDec}
          />
        </div>
      </div>
    </div>
  );
}
